import type {
  DirectiveResult,
  SYSActions,
  SYSDialog,
  SYSState,
  UnsafeHTMLDirective,
} from "../.."
import { ovl } from "../.."
import { TemplateResult } from "../../"

import { DialogResult } from "../actions"
import type { SYSFormState } from "../forms/fieldapi"
import { _DialogCancelType, _DialogOkType, type ResultType } from "./dialog"
import type {
  DialogsState,
  DialogType,
  FormHandlingType,
} from "./sysdialogbase"

export type EditorStart_Type = {
  formState: SYSFormState
  fieldKey: string
}

export type EditorStartContext_Type = {
  state: SYSState
  actions: SYSActions
}

type DialogOkCancelType = {
  defaultButton?: ResultType
  type?: DialogType
  customClass?: string
  okText?: _DialogOkType
  cancelText?: _DialogCancelType
  freeButton1Text?: string
  instance?: any
  title?: TemplateResult | string | DirectiveResult
  connectedForm?: { formType: string; formId: string }
}

export const DialogOkCancel = async (
  text: TemplateResult | string | DirectiveResult,
  options?: DialogOkCancelType
) => {
  let instance = options?.instance ? options.instance : "SYSModal"
  if (DialogIsOpen("SYSModal", instance)) {
    // make sure only 1 dialog of same type is opened
    return
  }

  ovl.getActions().ovl.dialog._OkCancelDialog({
    text,
    default: options?.defaultButton || 1,
    type: options?.type || "confirmation",
    customClass: options?.customClass,
    okText: options?.okText,
    cancelText: options?.cancelText,
    freeButton1Text: options?.freeButton1Text,
    instance,
    title: options?.title,
    connectedForm: options?.connectedForm,
  })

  return await DialogResult(instance)
}

type DialogOkType = {
  type?: DialogType
  customClass?: string
  okText?: _DialogOkType
  instance?: any
  title?: TemplateResult | string | UnsafeHTMLDirective
  connectedForm?: { formType: string; formId: string }
}

export const DialogOk = async (
  text: string | TemplateResult | DirectiveResult,
  options?: DialogOkType
) => {
  let instance = options?.instance ? options?.instance : "SYSModal"
  if (DialogIsOpen("SYSModal", instance)) {
    // make sure only 1 dialog of same type is opened
    return
  }
  ovl.getActions().ovl.dialog._OkDialog({
    text,
    type: options?.type || "information",
    customClass: options?.customClass,
    okText: options?.okText,
    instance,
    title: options?.title,
    connectedForm: options?.connectedForm,
  })

  return await DialogResult(instance)
}

export const IsDialogOpen = (dialogType: SYSDialog, instance?: any) => {
  let dlgMainState = ovl.getState().ovl.dialogs[dialogType]
  if (!instance) {
    return Object.keys(dlgMainState).some(
      (instance) =>
        dlgMainState[dialogType][instance].visible &&
        !dlgMainState[dialogType][instance].closing
    )
  }
  return (
    dlgMainState[dialogType][instance].visible &&
    !dlgMainState[dialogType][instance].closing
  )
}

export type OpenDialogOptionsType = {
  instance?: any
  // focus?: {
  //   persistFocus?: boolean
  //   forceElementIdToFocusAfterOpen?: string
  //   forceElementIdToFocusAfterClose?: string
  // }
  formsHandling?: FormHandlingType[]
  data?: any
}

export type CloseDialogOptionsType = {
  instance?: any
  forceElementIdToFocusAfterClose?: string
  resolveValue?: any
}

// export const SetResolveValueDialog = (dialogType: SYSDialog, value: any) => {
//   ovl.getState().ovl.dialogs[dialogType].resolveValue = value
// }

export const DialogState = (
  dialog?: SYSDialog,
  instance?: any
): DialogsState => {
  if (!dialog) {
    dialog = DialogCurrent()
  }
  if (!instance) {
    instance = dialog
  }
  return ovl.getState().ovl.dialogs[dialog]?.instance?.[instance]
}

export const DialogModal = async (
  dialogType: SYSDialog,
  options?: OpenDialogOptionsType
): Promise<any> => {
  ovl.getState().ovl.libState.dialog.result = undefined
  return await ovl.getActions().ovl.dialog._DialogOpen({
    dialogType,
    // forceElementIdToFocusAfterClose:
    //   options?.focus?.forceElementIdToFocusAfterClose,
    // forceElementIdToFocusAfterOpen:
    //   options?.focus?.forceElementIdToFocusAfterOpen,

    modal: true,
    formsHandling: options?.formsHandling,
    data: options?.data,
    instance: options?.instance,
  })
}
export const DialogCurrent = (
  dialogToIgnore: SYSDialog = undefined
): SYSDialog => {
  let res = DialogCurrentState()
    .filter((f) => f.dialog !== dialogToIgnore)
    .map((m) => m.dialog)
  if (res.length > 0) {
    return <SYSDialog>res[0]
  } else {
    return undefined
  }
}

/* returns DialogStates of currently open Dialog in order of zIndex Desc */
export const DialogCurrentState = (): DialogsState[] => {
  let dialogsState = ovl.getState().ovl.dialogs
  let res: DialogsState[] = []
  Object.keys(dialogsState).forEach((f) =>
    Object.keys(dialogsState[f].instance).forEach((instance) => {
      if (
        dialogsState[f].instance[instance].visible &&
        !dialogsState[f].instance[instance].closing
      ) {
        res.push(dialogsState[f].instance[instance])
      }
    })
  )
  res.sort((a, b) => b.zIndex - a.zIndex)
  return res
}

export const DialogIsOpen = (dialog: SYSDialog, instance?: string): boolean => {
  if (!instance) {
    instance = dialog
  }
  let dialogsState = ovl.getState().ovl.dialogs
  if (dialogsState[dialog].instance[instance] === undefined) {
    return false
  }
  return (
    dialogsState[dialog].instance[instance].visible &&
    !dialogsState[dialog].instance[instance].closing
  )
}

export const DialogOpen = async (
  dialogType: SYSDialog,
  options?: OpenDialogOptionsType
) => {
  await ovl.getActions().ovl.dialog._DialogOpen({
    dialogType,
    formsHandling: options?.formsHandling,
    instance: options?.instance,
  })
}

export type ResolveDialogOptionsType = {
  instance?: any
}

// needs more thinking (it works like this but not sure of the implications...)
// export const DialogResolve = async (
//   dialogType: SYSDialog,
//   resolveValue: any,
//   options?: ResolveDialogOptionsType
// ) => {
//   let instance = options?.instance
//   if (!instance) {
//     instance = dialogType
//   }
//   let state = ovl.getState()
//   let dlgState = state.ovl.dialogs[dialogType].instance[instance]
//   if (dlgState.modal && dlgState.resolver) {
//     dlgState.resolver(resolveValue)
//   }
// }
export const DialogClose = async (
  dialogType: SYSDialog,
  options?: CloseDialogOptionsType
) => {
  await ovl.getActions().ovl.dialog._DialogClose({
    dialog: dialogType,
    forceElementIdToFocusAfterClose: options?.forceElementIdToFocusAfterClose,
    resolveValue: options?.resolveValue,
    instance: options?.instance,
  })
}

export type DialogSetLockOptionsType = {
  instance?: any
}

export const DialogSetLock = (
  dialogType: SYSDialog,
  lock: boolean,
  options?: DialogSetLockOptionsType
) => {
  ovl.getActions().ovl.dialog._DialogSetLock({
    dialog: dialogType,
    locked: lock,
    instance: options?.instance,
  })
}
export type { _DialogHolderParams as DialogHolderParams } from "./sysdialogholder"
